import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, refreshCartMsg } from "../store/slices/guestSlices";
import { increaseQuantity, decreaseQuantity, removeFromCart } from "../store/slices/guestSlices";
import deleteIcon from '../icons/DeleteWhite.svg';
import PlusIcon from "../icons/plus-icon.svg";
import MinusIcon from "../icons/minus-icon.svg";
import high from '../icons/high.svg';
import low from '../icons/low.svg'
import categoryProductMarkup from "../constants/CategoryUpcMarkup";

export const getRevisedPrice = (title, price, categories = [], size = '', upc = '', categoryName = '', categoryIndex = -1) => {
  const base = parseFloat(price) || 0;
  const defaultPct = parseFloat(process.env.REACT_APP_PRODUCT_INCREMENT_PERCENT) || 0.4;

  const lcTitle = String(title || '').toLowerCase();
  const lcSize = String(size || '').toLowerCase();
  const isAdultBeverage = Array.isArray(categories) && categories.includes('Adult Beverage');
  const isBeverageCategory =
    Array.isArray(categories) &&
    categories.some(c => String(c).toLowerCase() === 'beverages');

  const PACK_FLOZ_REGEX = /\b\d+\s*(ct|pk)\s*\/\s*\d+\s*fl\.?\s*oz\b/;

  let revised = base;

  // Determine markup percentage - defaults to global markup from REACT_APP_PRODUCT_INCREMENT_PERCENT
  let pct = defaultPct;
  
  // Only apply custom markup if:
  // 1. Category name is provided
  // 2. Product index is valid (>= 0)
  // 3. Category has custom markup configuration
  // 4. Product is within the first N products (count) for that category
  if (categoryName && categoryIndex >= 0 && categoryProductMarkup[categoryName]) {
    const config = categoryProductMarkup[categoryName];
    if (config && config.count && config.markup !== undefined && categoryIndex < config.count) {
      pct = config.markup;
    }
    // If category exists but product is beyond the count, or config is invalid, 
    // pct remains as defaultPct (global markup)
  }
  // If no category configuration exists, pct remains as defaultPct (global markup)

  // Apply markup to everything except Adult Beverage
  if (!isAdultBeverage) {
    revised += revised * pct;
  }

  // Add surcharge if size has "bottles" OR title has "bottled water" OR "water bottles"
  const bottleSurcharge = parseFloat(process.env.REACT_APP_WATER_BOTTLE_SURCHARGE) || 5;
  const needsBottleSurcharge =
    lcSize.includes('bottles') ||
    lcTitle.includes('bottled water') ||
    lcTitle.includes('water bottles');

  if (needsBottleSurcharge) {
    revised += bottleSurcharge;
  }

  const gallonSurcharge = parseFloat(process.env.REACT_APP_WATER_GALLON_SURCHARGE) || 1.5;
  const needsGallonSurcharge =
    lcTitle.includes('gallon water') ||
    lcTitle.includes('purified water') ||
    lcTitle.includes('water jug');

  if (needsGallonSurcharge) {
    revised += gallonSurcharge;
  }

  const needsPackFlOzSurcharge =
    PACK_FLOZ_REGEX.test(lcSize) || PACK_FLOZ_REGEX.test(lcTitle);

  if (needsPackFlOzSurcharge && isBeverageCategory) {
    revised += 2; // adjust if you want a different surcharge
  }

  return revised.toFixed(2);
};

const NewProductCard = (props) => {
    // props
    const { title, imageLink, price, category, soldBy='', productWeight, _id: productId, availability, upc, _categoryName, _categoryIndex } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { cartItems } = useSelector((state) => state.cart);
    const company = localStorage.getItem('activeCompany');
    let companyData = null;
    try {
      companyData = company ? JSON.parse(company) : null;
    } catch (e) {
      console.error('Failed to parse company data:', e);
      companyData = null;
    }
    
    //consts
    const item = companyData && cartItems && cartItems.length > 0 && cartItems[companyData?.name] && cartItems[companyData?.name].filter((item) => productId === item.productId);
    
    // All hooks must be called before any early returns
    const [added, setAdded] = useState(!!(item && item[0]));
    const [count, setCount] = useState(item && item[0] ? item[0].quantity : 1);
    const [showControlsOnMobile, setShowControlsOnMobile] = useState(false);
    
    // Save cartItems to localStorage when cart changes
    useEffect(() => {
        if (cartItems) {
            localStorage.setItem('cartItems', JSON.stringify(cartItems));
        }
    }, [cartItems]);
    
    // Don't render if price is missing or zero
    if (!price || parseFloat(price) <= 0) {
        return null;
    }

    const size = (item && item[0] && item[0].size) ? item[0].size : productWeight || '';
    const categories = Array.isArray(category) ? category : (category ? [category] : []);
    const revisedPrice = getRevisedPrice(title, price, categories, size, upc || '', _categoryName || '', _categoryIndex !== undefined ? _categoryIndex : -1);
    
    // Ensure revisedPrice is a valid string before splitting
    if (!revisedPrice || typeof revisedPrice !== 'string' || parseFloat(revisedPrice) <= 0) {
        return null;
    }
    
    const priceParts = revisedPrice.split(".");
    const integerPart = priceParts[0] || '0';
    const decimalPart = priceParts[1] || '00';
    const available = (availability === 'In Stock');

    // Event Handlers
    const handleProductClick = (e) => {
        // Store category info for product detail page to use same markup
        if (_categoryName && _categoryIndex !== undefined) {
            localStorage.setItem(`productCategory_${productId}`, JSON.stringify({
                categoryName: _categoryName,
                categoryIndex: _categoryIndex
            }));
        }
        navigate(`/product/${productId}`)
        window.scrollTo(0, 0)
    };

    const handleCardClick = (e) => {
        // Check if the click was on a button (quantity controls)
        const target = e.target;
        const isControl = target.closest('button') || target.closest('[role="button"]');
        if (isControl) {
            // Let the button handle its own click
            return;
        }

        // Check if click is on title or meta (should navigate)
        const isTitleOrMeta = target.closest('[data-product-title]') || target.closest('[data-product-meta]');
        if (isTitleOrMeta) {
            handleProductClick(e);
            return;
        }

        // On mobile, if item is already added, show controls when tapping image area
        const isMobile = window.innerWidth < 768;
        if (isMobile && added) {
            // If clicking on image area, show controls
            const isImageArea = target.closest('[data-image-area]');
            if (isImageArea) {
                e.preventDefault();
                e.stopPropagation();
                setShowControlsOnMobile(true);
                return;
            }
        }
        
        // Default: navigate to product details
        handleProductClick(e);
    };

    const handleAddProduct = (e) => {
        e.stopPropagation();
        setAdded(true);
        const cartProduct = {
            productId,
            description: title,
            images: [imageLink],
            brand: companyData.name,
            quantity: 1,
            size: productWeight,
            price: revisedPrice,
            upc: upc || "",
            _categoryName: _categoryName || "",
            _categoryIndex: _categoryIndex !== undefined ? _categoryIndex : -1,
        };
      
        dispatch(addToCart(cartProduct));
        setTimeout(() => {
            dispatch(refreshCartMsg());
        }, 2000);
    };

    const handleDecrement = (e) => {
        e.stopPropagation();
        if (count === 1) {
            setAdded(false);
            setShowControlsOnMobile(false);
            dispatch(removeFromCart({ productId }));
            setTimeout(() => {
              dispatch(refreshCartMsg());
            }, 2000);
            return;
        }
        setCount(count - 1);
        dispatch(decreaseQuantity(productId));
    };

    const handleIncrement = (e) => {
        e.stopPropagation();
        setCount(count + 1);
        dispatch(increaseQuantity(productId));
    };

    const getStockLevel = () => {
        const stockLevel = availability;
        const stockComponent = ({ img, label }) => (
            <div className="NewProductCard__StockLevel mobile:w-[35px] w-[100px] h-[35px] absolute bottom-[-10px] right-[-27px] shadow-[0_4px_18px_0_rgba(0,0,0,0.12)] rounded-full bg-white flex items-center justify-evenly mobile:right-0">
                <img src={img} alt={label} className="NewProductCard__StockLevel__Img w-[15px] h-[15px] inline-block" />
                <span className="mobile:hidden tablet:hidden NewProductCard__StockLevel__Label text-[12px] font-bold">{label}</span>
            </div>
        );
        let props = {
            img: null,
            label: ''
        };

        if (stockLevel === 'In Stock') {
            props.img = high;
            props.label = 'High Stock'
        } else {
            return null;
        }

        return stockComponent(props);
    };

    // Get the state of the add button trash/minus
    const getAddButtonState = () => {
        const isMobile = window.innerWidth < 768;
        const shouldShowControls = added && (isMobile ? showControlsOnMobile : true);
        
        const parentContainer = ({ children }) => {
            return (
                <div
                    onClick={!added ? handleAddProduct : undefined}
                    className={`group flex absolute bg-[#2A2A2A] rounded-full h-[35px] tablet:h-[35px] w-[35px] items-center justify-center transition-all duration-500 text-[30px] shadow-[0_6px_18px_rgba(15,23,42,0.12)] laptop:hover:w-[120px] ${shouldShowControls ? 'mobile:w-[110px] tablet:w-[110px]' : 'tablet:w-[35px]'} overflow-hidden cursor-pointer`}
                >
                        {children}
                </div>
            );
        };
        let buttonAddElement;
        if (!added || !shouldShowControls) {
            buttonAddElement = (
                <div className="laptop:min-w-fit">
                    <button className="laptop:group-hover:hidden laptop:group-hover:pr-2 tablet:text-[22px] flex items-center cursor-pointer">
                        <img className="NewProductCard__AddButton__Plus w-[25px] h-[25px]" src={PlusIcon} alt="plusIcon" />
                    </button>
                    <button className="hidden text-base1 font-bold laptop:group-hover:block px-3 laptop:min-w-[120px] cursor-pointer overflow-hidden text-white">
                        Add to cart
                    </button>
              </div>
            )
        } else {
            buttonAddElement = (
                <div className="flex items-center w-full">
                    <button
                        onClick={handleDecrement}
                        className="block w-[40px] cursor-pointer h-[50px] rounded-l-full pb-1"
                    >
                        {
                            (count === 1)
                                ? (
                                    <img className="w-full p-2 filter" src={deleteIcon} alt="delete" />
                                )
                                : (
                                    <img className="h-[25px] w-[25px] ml-[15px]" src={MinusIcon} alt="decreaseProduct" /> 
                                )
                        }
                    </button>
                    <label
                        className="text-[20px] block px-4 text-white cursor-pointer"
                    >
                        {count}
                    </label>
                    <button
                        onClick={handleIncrement}
                        className="block w-[40px] cursor-pointer h-[50px] rounded-r-full pb-1"
                    >
                        <img className="NewProductCard__AddButton__Plus w-[25px] h-[25px] hover:fill-black" src={PlusIcon} alt="plusIcon" />
                    </button>
                </div>
            )
        }

        return parentContainer({ children: buttonAddElement });
    };

    console.log(title, ' => ', imageLink);

    return (
        <div 
            onClick={handleCardClick}
            className="NewProductCard w-[100%] h-[100%] flex flex-col items-start laptop:cursor-pointer mobile:items-center"
        >
            <div 
                data-image-area
                className="NewProductCard__Img h-[58%] w-[88%] bg-contain bg-center bg-no-repeat relative"
                onClick={(e) => {
                    const isMobile = window.innerWidth < 768;
                    if (isMobile && added) {
                        e.stopPropagation();
                        setShowControlsOnMobile(true);
                    }
                }}
            >
                {
                    available
                        ? (
                            getAddButtonState()
                        )
                        : (
                            <div
                                className="NewProductCard__OutOfStock absolute w-[100px] h-[35px] rounded-full bg-[#EF4949] flex items-center justify-center shadow-[0_6px_18px_rgba(239,73,73,0.35)]">
                                <p className="absolute font-semibold text-[12px] text-white">Out of stock</p>
                            </div>
                        )
                }
                {
                    getStockLevel()
                }
                <div className='flex justify-center'>
                    <img src={imageLink} alt={title} className='w-32 h-auto'/>
                </div>
            </div>

            <div
                className="mt-8 NewProductCard__Price mobile:text-base1 tablet:text-base2 text-[17px] font-bold font-segoe text-[#111827]">
                <sup className="mobile:text-[10px] text-[12px]">$</sup>
                {integerPart}
                <sup className="mobile:text-[10px] text-[9px] font-semibold inline-block ml-[1px]">{decimalPart}</sup> usd
            </div>
            <div 
                data-product-title
                className="NewProductCard__Description mobile:text-base1 tablet:text-base1 font-segoe opacity-95 max-w-full cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    handleProductClick(e);
                }}
            >
                <p className="text-[12px] mobile:text-[8px] text-[#111827] hover:text-[#FF5A5F] transition-colors">{title}</p>
            </div>
            <div 
                data-product-meta
                className="NewProductCard__SoldBy text-[#6B7280] text-[12px] font-segoe cursor-pointer hover:text-[#4B5563] transition-colors"
                onClick={(e) => {
                    e.stopPropagation();
                    handleProductClick(e);
                }}
            >
                (${revisedPrice} / {soldBy === 'WEIGHT' ? 'lb': soldBy ? soldBy.toLowerCase() : ''})
            </div>
            <div 
                data-product-meta
                className="NewProductCard__Size text-[#6B7280] text-[12px] font-segoe cursor-pointer hover:text-[#4B5563] transition-colors"
                onClick={(e) => {
                    e.stopPropagation();
                    handleProductClick(e);
                }}
            >
                {productWeight}
            </div>
        </div>
    )
};

export default NewProductCard;
